import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigService } from '../../../../core/services/config/config.service';
import { UtilityService } from '../../../../core/services/utility/utility.service';
import { QuestionnaireDetail } from '../../models/questionnaire-detail';
import { QuestionnaireHistoryEntry } from '../../models/questionnaire-history-entry';
import { ScreeningDetail } from '../../models/screening-detail';
import { ScreeningHistoryEntry } from '../../models/screening-history-entry';
import { ScreeningsSummary } from '../../models/screenings-summary';
import { StudyReminderDetail } from '../../models/study-reminder-detail';

/**
 * Read-only Screenings feature service. Spans the three PatientPortal controllers backing the
 * vertical (PatientScreenings, PatientQuestionnaires, PatientStudyReminders). Every request opts
 * out of the global error page via `skipGlobalError` so each view can render its own inline error
 * state + retry; the interceptor honors the token method-agnostically.
 *
 * NOTE: the Screenings components are still scaffolds, so nothing renders that error state yet —
 * PBI 53292 ("Screenings - Error API unavailable") owes it. Until it lands, a failed read here is
 * silent: the opt-out has suppressed the global error page but no inline state replaces it.
 */
@Injectable({
  providedIn: 'root',
})
export class ScreeningsService {
  private utilityService = inject(UtilityService);
  private configService = inject(ConfigService);
  private config = this.configService.config;

  patientScreeningsEndpoint = `${this.config.patientApiUrl}/v1/PatientPortal/PatientScreenings`;
  patientQuestionnairesEndpoint = `${this.config.patientApiUrl}/v1/PatientPortal/PatientQuestionnaires`;
  patientStudyRemindersEndpoint = `${this.config.patientApiUrl}/v1/PatientPortal/PatientStudyReminders`;

  getSummary(patientId: number): Observable<ScreeningsSummary> {
    const url = `${this.patientScreeningsEndpoint}/Summary?patientId=${patientId}`;
    return this.utilityService.getRequest<ScreeningsSummary>(url, { skipGlobalError: true });
  }

  getQuestionnaireHistory(patientId: number, templateKey: string): Observable<QuestionnaireHistoryEntry[]> {
    const url = `${this.patientQuestionnairesEndpoint}?patientId=${patientId}&templateKey=${encodeURIComponent(templateKey)}`;
    return this.utilityService.getRequest<QuestionnaireHistoryEntry[]>(url, { skipGlobalError: true });
  }

  getQuestionnaireDetail(patientId: number, patientQuestionnaireId: number): Observable<QuestionnaireDetail> {
    const url = `${this.patientQuestionnairesEndpoint}/${patientQuestionnaireId}?patientId=${patientId}`;
    return this.utilityService.getRequest<QuestionnaireDetail>(url, { skipGlobalError: true });
  }

  getScreeningHistory(patientId: number, conceptKey: string): Observable<ScreeningHistoryEntry[]> {
    const url = `${this.patientScreeningsEndpoint}?patientId=${patientId}&conceptKey=${encodeURIComponent(conceptKey)}`;
    return this.utilityService.getRequest<ScreeningHistoryEntry[]>(url, { skipGlobalError: true });
  }

  getScreeningDetail(patientId: number, patientScreeningId: number): Observable<ScreeningDetail> {
    const url = `${this.patientScreeningsEndpoint}/${patientScreeningId}?patientId=${patientId}`;
    return this.utilityService.getRequest<ScreeningDetail>(url, { skipGlobalError: true });
  }

  getReminderDetail(patientId: number, patientStudyReminderId: number): Observable<StudyReminderDetail> {
    const url = `${this.patientStudyRemindersEndpoint}/${patientStudyReminderId}?patientId=${patientId}`;
    return this.utilityService.getRequest<StudyReminderDetail>(url, { skipGlobalError: true });
  }
}
